import AdsBackground from "@/assets/img/advisor-inner/ads_background.webp"
import StartChatMark from "@/assets/svg/advisor-inner/start-chat-mark.svg?react"
import Dot from "@/assets/svg/advisors/Dot.svg?react"

import { advisorInnerCopy } from "../config/advisor-inner"
import type { Advisor } from "../types"

interface AdvisorTrustedGuideProps {
  advisor: Advisor
  onChat: (advisor: Advisor) => void
  /** Override the bubble text; defaults to the advisor's intro message. */
  message?: string
  /** Background art; defaults to the advisor-inner promo art. */
  bgImage?: string
  /** Heading (supports `\n`); defaults to the advisor-inner copy. */
  title?: string
  subtitle?: string
  buttonLabel?: string
}

/** "Meet your trusted guide" promo block with a glass card. Ported from AdvisorsInner TrustedGuide. */
export function AdvisorTrustedGuide({
  advisor,
  onChat,
  message,
  bgImage = AdsBackground,
  title = advisorInnerCopy.trustedGuide.title,
  subtitle = advisorInnerCopy.trustedGuide.subtitle,
  buttonLabel = advisorInnerCopy.trustedGuide.button,
}: AdvisorTrustedGuideProps) {
  return (
    <div
      data-slot="advisor-trusted-guide"
      className="relative flex w-full flex-col overflow-hidden"
    >
      <img
        src={bgImage}
        alt=""
        className="pointer-events-none absolute -top-5 right-0 h-[404px] w-full object-cover object-right"
      />

      <div className="relative z-[1] mx-4 mt-[99px] max-w-[52%]">
        <h3 className="whitespace-pre-line font-display text-2xl font-medium text-purple-light">
          {title}
        </h3>
        <p className="mt-3 whitespace-pre-line font-body text-base text-purple-light/70">
          {subtitle}
        </p>
      </div>

      <div className="relative z-10 mx-4 mt-6 rounded-3xl bg-purple-light/10 p-4 backdrop-blur-[15px]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex size-13 min-w-13 items-center justify-center rounded-full bg-gradient-to-br from-peach to-purple-accent">
              <img
                src={advisor.photo}
                alt={advisor.name}
                className="size-12 rounded-full border-4 border-[#221c32] object-cover"
              />
            </div>
            <div>
              <p className="font-display text-sm font-medium text-white">{advisor.name}</p>
              <p className="font-body text-xs text-purple-light/70">{advisor.exp}</p>
            </div>
          </div>

          <div className="flex items-center gap-1 rounded-2xl bg-green/20 py-1.5 pl-2.5 pr-3 font-body text-xs text-green [&_svg]:size-2">
            <Dot /> Online
          </div>
        </div>

        <div className="relative mt-3">
          <span className="absolute -bottom-1 -left-[5px] w-3.5 [&_svg]:w-3.5">
            <StartChatMark />
          </span>
          <div className="rounded-[20px] bg-[#382e4b] px-4 py-3 text-purple-light">
            <p className="font-body text-base">{message ?? advisor.message}</p>
          </div>
        </div>

        <div className="flex w-full justify-end">
          <button
            type="button"
            onClick={() => onChat(advisor)}
            className="mt-4 flex h-14 w-[184px] items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white transition-opacity hover:opacity-80"
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
